/* ============================================================
   Watch Reserves — welcome offer popup.

   Shows once per visitor, after they have shown some engagement, and
   trades an email for a single-use store-credit code.

   Deliberately not on page load: Shopify's performance guidance treats an
   immediate dialog as an anti-pattern — Google penalises intrusive
   interstitials on mobile and the dialog competes with LCP. Waiting for
   scroll depth or dwell time also means the visitors who see it are the
   ones who stayed.

   Order on submit: the Railway relay mints the code first, then Klaviyo
   gets the subscription with the code on the profile (wr_welcome_code and
   friends), so the welcome flow's first email can print it. The profile
   properties arrive in the same request as the list join, which is what
   triggers the flow — no race between "joined list" and "has a code".

   If the relay fails, Klaviyo still gets the subscription (without a
   code) and the visitor is told plainly that the code did not generate,
   so they can try again. If Klaviyo is blocked — ad blockers reach it
   often — the visitor still sees their code, because losing the sale to
   protect the mailing list is the wrong trade.

   On reveal the code is also applied to the session (/discount/CODE sets
   the checkout discount cookie), and its real expiry is stored for the
   cart countdown in wr-credit-timer.js.

   The email is also passed to Klaviyo's onsite identify (Oct 2026), so
   this browser's later Viewed Product / Added to Cart events land on the
   subscriber's profile and can trigger the browse and cart flows. The
   subscriptions endpoint alone does not tie the browser to the profile.
   ============================================================ */
(function () {
  'use strict';

  var dialog = document.getElementById('wr-welcome-offer');
  if (!dialog) return;

  var SEEN_KEY = 'wr-offer-seen';
  var CODE_KEY = 'wr-offer-code';

  function stored(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function store(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }

  // Already subscribed, dismissed, or holding a code: never ask again.
  if (stored(SEEN_KEY)) return;

  var form = dialog.querySelector('[data-wr-offer-form]');
  var input = dialog.querySelector('#wr-offer-email');
  var button = dialog.querySelector('[data-wr-offer-submit]');
  var note = dialog.querySelector('[data-wr-offer-note]');
  var ask = dialog.querySelector('[data-wr-offer-ask]');
  var done = dialog.querySelector('[data-wr-offer-done]');
  var codeEl = dialog.querySelector('[data-wr-offer-code]');
  var termsEl = dialog.querySelector('[data-wr-offer-terms]');
  var busy = false;

  /* ── Tracking ──────────────────────────────────────── */
  // Reports armed / opened / closed / claimed to the relay so the timing can
  // be judged on data. A random per-browser id, never the email.
  var startedAt = Date.now();
  var claimed = false;
  var vid = stored('wr-vid');
  if (!vid) {
    vid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    store('wr-vid', vid);
  }

  function meta(trigger) {
    return {
      vid: vid,
      trigger: trigger,
      seconds: Math.round((Date.now() - startedAt) / 100) / 10,
      delay: Number(dialog.dataset.delay) || 15,
      scroll: Number(dialog.dataset.scroll) || 45,
      device: window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop',
      path: window.location.pathname,
      claimed: claimed
    };
  }

  var openedBy;
  function track(event) {
    try {
      var url = dialog.dataset.endpoint.replace(/\/$/, '') + '/offer-event';
      // text/plain keeps the beacon CORS-safe; the relay parses it as JSON.
      var body = JSON.stringify({ event: event, meta: meta(openedBy) });
      if (!(navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' })))) {
        fetch(url, { method: 'POST', body: body, keepalive: true }).catch(function () {});
      }
    } catch (e) { /* tracking must never break the pop-up */ }
  }

  // Once per session: a visitor who could still be shown the offer.
  try {
    if (!window.sessionStorage.getItem('wr-offer-armed')) {
      window.sessionStorage.setItem('wr-offer-armed', '1');
      track('arm');
    }
  } catch (e) { /* private mode */ }

  function say(message) {
    if (!note) return;
    note.textContent = message || '';
    note.hidden = !message;
  }

  /* ── Trigger ───────────────────────────────────────── */
  var opened = false;

  function open(trigger) {
    if (opened || dialog.open) return;
    // Don't interrupt someone mid-checkout-decision with the cart open.
    var cart = document.getElementById('cart-drawer');
    if (cart && cart.open) return;
    // Nor on top of the Notify me pop-up; the scroll trigger retries later.
    var notify = document.getElementById('wr-notify-modal');
    if (notify && notify.open) return;
    opened = true;
    openedBy = trigger;
    dialog.showModal();
    teardown();
    track('view');
  }

  function scrolled() {
    var h = document.documentElement;
    var max = h.scrollHeight - h.clientHeight;
    if (max <= 0) return 0;
    return ((window.scrollY || h.scrollTop) / max) * 100;
  }

  var threshold = Number(dialog.dataset.scroll) || 45;
  var delay = (Number(dialog.dataset.delay) || 15) * 1000;

  function onScroll() {
    if (scrolled() >= threshold) open('scroll');
  }

  var timer = window.setTimeout(function () { open('timer'); }, delay);
  window.addEventListener('scroll', onScroll, { passive: true });

  function teardown() {
    window.clearTimeout(timer);
    window.removeEventListener('scroll', onScroll);
  }

  // Dismissing counts as an answer — don't ask again on the next page.
  dialog.addEventListener('close', function () {
    track('close');
    store(SEEN_KEY, '1');
  });

  /* ── Klaviyo ───────────────────────────────────────── */
  // Ties this browser to the email so onsite events reach the profile.
  // Needs the Klaviyo app embed; a no-op if it is off or blocked.
  function identify(email) {
    try {
      if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
        window.klaviyo.identify({ email: email });
      } else {
        (window._learnq = window._learnq || []).push(['identify', { $email: email }]);
      }
    } catch (e) { /* never block the code on this */ }
  }

  // payload is the relay's response, or null if no code was minted.
  function subscribe(email, payload) {
    var company = dialog.dataset.company;
    if (!company) return Promise.resolve();

    var attributes = {
      email: email,
      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
    };
    if (payload && payload.code) {
      attributes.properties = {
        wr_welcome_code: payload.code,
        wr_welcome_credit: payload.amount,
        wr_welcome_minimum: payload.minimum,
        wr_welcome_expires: payload.expires
      };
    }

    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + company, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2025-01-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: dialog.dataset.source,
            profile: { data: { type: 'profile', attributes: attributes } }
          },
          relationships: { list: { data: { type: 'list', id: dialog.dataset.list } } }
        }
      })
    }).catch(function () { /* see header: never block the code on this */ });
  }

  /* ── Reveal ────────────────────────────────────────── */
  // "30 minutes" / "48 hours" / "30 days", from the relay's real endsAt
  // rather than a number copied into the theme that can drift from the
  // env var.
  function validFor(expires) {
    var ms = Date.parse(expires || '') - Date.now();
    if (!(ms > 0)) return '';
    var minutes = Math.round(ms / 60000);
    if (minutes < 90) return minutes + ' minutes';
    var hours = Math.round(ms / 3600000);
    return hours <= 72 ? hours + ' hours' : Math.round(hours / 24) + ' days';
  }

  function reveal(payload) {
    if (codeEl) codeEl.textContent = payload.code;
    var window_ = validFor(payload.expires);
    if (termsEl) {
      termsEl.textContent =
        '$' + payload.amount + ' credit on orders over $' + payload.minimum +
        '. Single use' + (window_ ? ', valid for ' + window_ : '') +
        '. Tap the code to copy it again.';
    }
    if (ask) ask.hidden = true;
    if (done) done.hidden = false;

    store(SEEN_KEY, '1');
    store(CODE_KEY, payload.code);
    if (payload.expires) store('wr-offer-expires', payload.expires);
    store('wr-offer-amount', String(payload.amount));
    store('wr-offer-minimum', String(payload.minimum));

    // Put the credit on this session's checkout so nobody has to paste it.
    fetch('/discount/' + encodeURIComponent(payload.code), { credentials: 'same-origin' }).catch(function () {});

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(payload.code).catch(function () { /* needs a gesture in some browsers */ });
    }
    if (window.wrAnnounce) window.wrAnnounce('Your $' + payload.amount + ' credit code is ' + payload.code);
  }

  if (codeEl) {
    codeEl.addEventListener('click', function () {
      if (!navigator.clipboard || !codeEl.textContent) return;
      navigator.clipboard.writeText(codeEl.textContent).then(function () {
        var was = codeEl.textContent;
        codeEl.textContent = 'Copied';
        window.setTimeout(function () { codeEl.textContent = was; }, 1200);
      }).catch(function () { /* nothing useful to say if it fails */ });
    });
  }

  /* ── Submit ────────────────────────────────────────── */
  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (busy) return;

      var email = (input && input.value || '').trim();
      if (!email || email.indexOf('@') === -1 || email.indexOf('.') === -1) {
        say('Please enter a valid email address.');
        if (input) input.focus();
        return;
      }

      var honeypot = form.querySelector('input[name="company"]');
      busy = true;
      say('');
      if (button) { button.disabled = true; button.textContent = '...'; }

      fetch(dialog.dataset.endpoint.replace(/\/$/, '') + '/discount', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email, company: honeypot ? honeypot.value : '', meta: meta(openedBy) })
      })
        .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
        .then(function (res) {
          if (!res.ok || !res.body || !res.body.code) {
            throw new Error((res.body && res.body.error) || 'No code returned');
          }
          claimed = true;
          subscribe(email, res.body);
          identify(email);
          reveal(res.body);
        })
        .catch(function () {
          // Keep them on the list, but don't promise a code that was never
          // minted — and leave SEEN unset so a retry is possible.
          track('claim_fail');
          subscribe(email, null);
          identify(email);
          say('You are on the list, but your code did not generate. Please try again in a moment.');
        })
        .finally(function () {
          busy = false;
          if (button) { button.disabled = false; button.textContent = 'Unlock'; }
        });
    });
  }
})();
